import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, ShoppingBag, Download, Sparkles, Briefcase, Share2, Hotel, HeartPulse } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import InstallAppButton from "@/components/InstallAppButton";
import Seo from "@/components/Seo";
import AdminEditableImage from "@/components/AdminEditableImage";
import { useCategories } from "@/hooks/useCategories";
import { useSiteContent, scValue, useCmsItems } from "@/hooks/useSiteContent";
import { blogImage, blogLang } from "@/lib/blog";
import { categoryGraphics } from "@/lib/categoryGraphics";
import { categoryName } from "@/lib/helpers";
import { SITE_NAME, SITE_ORIGIN, DEFAULT_OG_IMAGE, SUPPORT_EMAIL } from "@/lib/seo";

const Home = () => {
  const { t, lang } = useI18n();
  const { categories } = useCategories();
  const sc = useSiteContent();
  const hero = sc?.hero;
  const home = sc?.home;
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  // Valore CMS con fallback ai testi i18n (sito identico prima di ogni modifica).
  const heroV = (base, tkey) => scValue(hero, base, lang, t(tkey));
  const homeV = (base, tkey) => scValue(home, base, lang, t(tkey));
  const heroImg = hero?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${hero.image_url}` : "/logo-lsh-transparent.png";
  const steps = useCmsItems("home_step");
  const blocks = useCmsItems("home_block");
  const stepIcons = [Search, ShoppingBag, Download];
  const cmsL = (d, base) => (lang === "en" ? d[`${base}_en`] : d[`${base}_it`]) || d[`${base}_it`] || d[`${base}_en`] || "";
  const hb = sc?.home_blog;
  const blogVisible = hb ? hb.visible !== false : true;
  const homeBlogV = (base) => scValue(hb, base, lang, base === "title" ? (lang === "en" ? "From the Blog" : "Dal Blog") : "");
  const [blogPosts, setBlogPosts] = useState([]);
  useEffect(() => {
    api.get("/blog/latest", { params: { limit: 3 } }).then(({ data }) => setBlogPosts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then(({ data }) => {
        if (data && data.length) { setProducts(data.slice(0, 4)); }
        else { api.get("/products").then(({ data }) => setProducts(data.slice(0, 4))).catch(() => {}); }
      })
      .catch(() => { api.get("/products").then(({ data }) => setProducts(data.slice(0, 4))).catch(() => {}); });
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title={`${SITE_NAME} — ${heroV("title", "hero.title").replace(/\.\s*$/, "")}`}
        description={heroV("subtitle", "hero.subtitle")}
        path="/"
        type="website"
        jsonLd={[
          { "@context": "https://schema.org", "@type": "Organization", name: SITE_NAME, url: SITE_ORIGIN, logo: DEFAULT_OG_IMAGE, email: SUPPORT_EMAIL },
          { "@context": "https://schema.org", "@type": "WebSite", name: SITE_NAME, url: SITE_ORIGIN },
        ]}
      />
      {/* HERO */}
      <section className="relative bg-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-electric/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading tracking-tight leading-[1.05]">
              {heroV("title", "hero.title")}<br />
              <span className="text-gradient-brand">{heroV("titleAccent", "hero.titleAccent")}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">{heroV("subtitle", "hero.subtitle")}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-products" onClick={() => navigate("/prodotti")}
                className="rounded-full h-12 px-7 bg-brand-electric hover:bg-brand-electric/90 text-white text-base gap-2 hover:-translate-y-0.5 transition-transform">
                {heroV("cta1", "hero.cta1")} <ArrowRight className="h-4 w-4" />
              </Button>
              <Button data-testid="hero-cta-smart" onClick={() => navigate("/servizi-smart")} variant="outline"
                className="rounded-full h-12 px-7 border-white/25 bg-transparent text-white hover:bg-white/10 text-base">
                {heroV("cta2", "hero.cta2")}
              </Button>
              <InstallAppButton />
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
            className="hidden lg:flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-electric/25 blur-3xl rounded-full" />
              <img src={heroImg} alt="LearnSphereHub" className="relative w-80 h-80 object-contain drop-shadow-2xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-navy tracking-tight">{homeV("categoriesTitle", "home.categoriesTitle")}</h2>
          <p className="mt-3 text-brand-navy/60">{homeV("categoriesSub", "home.categoriesSub")}</p>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => {
            const g = categoryGraphics(c.slug);
            const Icon = g.Icon;
            const bg = c.image_url ? `${process.env.REACT_APP_BACKEND_URL}${c.image_url}` : g.img;
            return (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i, 6) * 0.08 }}>
                <Link to={`/categoria/${c.slug}`} data-testid={`home-cat-${c.slug}`}
                  style={{ backgroundColor: "#081021", ...(bg ? { backgroundImage: `url(${bg})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" } : {}) }}
                  className="group relative overflow-hidden block h-full rounded-2xl border border-brand-navy/10 p-6 lift">
                  <div className="absolute inset-0" aria-hidden="true" style={{ background: "linear-gradient(90deg, rgba(5,13,31,0.50) 0%, rgba(5,13,31,0.20) 42%, rgba(5,13,31,0.04) 68%, rgba(5,13,31,0) 100%)" }} />
                  <div className="relative">
                    <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center text-white">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-heading font-semibold text-lg text-white">{categoryName(c, lang)}</h3>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm text-brand-electric font-medium">{t("common.explore")} <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-brand-light py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-navy tracking-tight">{homeV("featuredTitle", "home.featuredTitle")}</h2>
              <p className="mt-3 text-brand-navy/60">{homeV("featuredSub", "home.featuredSub")}</p>
            </div>
            <Button data-testid="home-all-products" onClick={() => navigate("/prodotti")} variant="outline" className="rounded-full text-brand-navy border-brand-navy/20">
              {t("common.discover")} <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24">
        <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-navy tracking-tight text-center">{homeV("howTitle", "home.howTitle")}</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {(steps.length ? steps : [{ id: "f1" }, { id: "f2" }, { id: "f3" }]).map((s, i) => {
            const Icon = stepIcons[i % stepIcons.length];
            const title = steps.length ? cmsL(s.data, "title") : homeV(`how${i + 1}Title`, `home.how${i + 1}Title`);
            const text = steps.length ? cmsL(s.data, "text") : homeV(`how${i + 1}`, `home.how${i + 1}`);
            return (
              <div key={s.id} data-testid={`how-step-${i}`} className="rounded-2xl border border-brand-navy/10 bg-white p-8 text-center">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-orange/10 flex items-center justify-center text-brand-orange">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-heading font-semibold text-lg text-brand-navy">{title}</h3>
                <p className="mt-2 text-sm text-brand-navy/60">{text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOME BLOCKS (info / cta / promo) — dinamici da CMS */}
      {blocks.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4 md:pb-8 space-y-5" data-testid="home-blocks">
          {blocks.map((b) => {
            const kind = b.data.kind || "info";
            const title = cmsL(b.data, "title");
            const text = cmsL(b.data, "text");
            const btnLabel = cmsL(b.data, "button_label");
            const btnUrl = b.data.button_url;
            const isExt = /^https?:\/\//i.test(btnUrl || "");
            const styles = {
              info: "bg-brand-light border border-brand-navy/10 text-brand-navy",
              cta: "bg-brand-navy text-white",
              promo: "bg-brand-orange/10 border border-brand-orange/30 text-brand-navy",
            }[kind] || "bg-brand-light text-brand-navy";
            return (
              <div key={b.id} data-testid={`home-block-${kind}`} className={`rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 ${styles}`}>
                <div className="max-w-2xl">
                  {kind === "promo" && <span className="inline-block mb-2 text-xs font-bold uppercase tracking-widest text-brand-orange">Promo</span>}
                  <h3 className="text-2xl font-bold font-heading">{title}</h3>
                  {text && <p className={`mt-2 ${kind === "cta" ? "text-white/70" : "text-brand-navy/60"}`}>{text}</p>}
                </div>
                {btnLabel && btnUrl && (
                  isExt
                    ? <a href={btnUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-full h-12 px-7 inline-flex items-center gap-2 bg-brand-electric hover:bg-brand-electric/90 text-white font-semibold">{btnLabel} <ArrowRight className="h-4 w-4" /></a>
                    : <Button onClick={() => navigate(btnUrl)} className="shrink-0 rounded-full h-12 px-7 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">{btnLabel} <ArrowRight className="h-4 w-4" /></Button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* DAL BLOG */}
      {blogVisible && blogPosts.length > 0 && (
        <section className="bg-brand-light" data-testid="home-blog">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold font-heading text-brand-navy tracking-tight">{homeBlogV("title")}</h2>
                <p className="mt-3 text-brand-navy/60">{homeBlogV("subtitle")}</p>
              </div>
              <Button data-testid="home-blog-cta" onClick={() => navigate("/blog")} variant="outline"
                className="rounded-full h-11 px-6 border-brand-navy/20 text-brand-navy gap-2">
                {lang === "en" ? "Go to Blog" : "Vai al Blog"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogPosts.map((p) => {
                const bimg = blogImage(p.image_url);
                return (
                  <div key={p.id} onClick={() => navigate(`/blog/${p.slug}`)} data-testid={`home-blog-card-${p.slug}`}
                    className="group cursor-pointer bg-white rounded-2xl border border-brand-navy/10 overflow-hidden lift flex flex-col">
                    <div className="aspect-[16/9] bg-brand-light overflow-hidden">
                      {bimg ? <img src={bimg} alt={blogLang(p, "title", lang)} loading="lazy" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading font-semibold text-brand-navy leading-snug line-clamp-2">{blogLang(p, "title", lang)}</h3>
                      <p className="mt-2 text-sm text-brand-navy/60 line-clamp-2 flex-1">{blogLang(p, "excerpt", lang)}</p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-electric group-hover:text-brand-orange">{lang === "en" ? "Read article" : "Leggi l'articolo"} <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SMART TEASER */}
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-24 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="mt-4 text-3xl md:text-4xl font-bold font-heading tracking-tight">{homeV("smartTitle", "home.smartTitle")}</h2>
            <p className="mt-3 text-white/70">{homeV("smartSub", "home.smartSub")}</p>
          </div>
          <Button data-testid="home-smart-cta" onClick={() => navigate("/servizi-smart")}
            className="rounded-full h-12 px-7 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2 shrink-0">
            {t("common.discoverSmart")} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
